import { signal } from "./core/reactive.js";
import { api, setToken, onAuthError } from "./api.js";
import * as outbox from "./core/outbox.js";

export const profile = signal(null);
export const muscles = signal([]); // [{muscle_id, muscle, ...}]
export const activeSession = signal(null); // full active session object or null
export const pendingRequests = signal(0); // incoming friend requests (drives the nav badge)

// ── A workout that's finished here but not yet on the server ──────────────────
// Its completion is sitting in the outbox. The workout is over as far as the user is concerned
// — no active-session bar, no resume card — but claiming it's saved would be a lie, so the app
// shell says so on every screen until the queue drains. A signal (not just a queue lookup) so
// the chrome effect re-renders on it; mirrored to localStorage so a reload mid-flight doesn't
// quietly drop the claim while the op is still queued.
const FINISHING_KEY = "wt_finishing";
export const finishingSession = signal(readFinishing());

function readFinishing() {
  try { return localStorage.getItem(FINISHING_KEY) || null; } catch { return null; }
}
export function markFinishing(name) {
  const label = name || "Workout";
  try { localStorage.setItem(FINISHING_KEY, label); } catch {}
  finishingSession(label);
}
function clearFinishing() {
  try { localStorage.removeItem(FINISHING_KEY); } catch {}
  finishingSession(null);
}
// The queue is the source of truth: the moment the completion has actually been sent (or was
// dropped as unsendable), the claim goes away. Checked once at load too, so a stale flag left
// by a crash between the send and this callback doesn't outlive the thing it described.
function reconcileFinishing() {
  if (finishingSession() && !outbox.hasPending("complete")) clearFinishing();
}
outbox.onChange(reconcileFinishing);
reconcileFinishing();

export async function refreshPendingRequests() {
  try {
    pendingRequests((await api.friendRequests()).incoming.length);
  } catch {
    pendingRequests(0);
  }
  return pendingRequests();
}

const muscleIndex = { byId: new Map(), byName: new Map() };

export function muscleById(id) {
  return muscleIndex.byId.get(Number(id)) || null;
}
export function muscleName(id) {
  return muscleById(id)?.muscle || "";
}
export function muscleByName(name) {
  return muscleIndex.byName.get(String(name).toLowerCase()) || null;
}

export async function loadMuscles() {
  if (muscles().length) return muscles();
  const list = await api.muscles();
  muscleIndex.byId.clear();
  muscleIndex.byName.clear();
  for (const m of list) {
    muscleIndex.byId.set(m.muscle_id, m);
    muscleIndex.byName.set(m.muscle.toLowerCase(), m);
  }
  muscles(list);
  return list;
}

export async function loadProfile() {
  const p = await api.me();
  profile(p);
  if (p?.gender === "female" || p?.gender === "male") {
    setBodyMap(p.gender);
  } else {
    try { localStorage.removeItem("body_map"); } catch {}
  }
  applyTheme(p.theme || "system");
  return p;
}

// Cache the active session so a reload with no signal still shows the in-progress
// workout (offline edits live in the outbox; this keeps the view alive too).
const ACTIVE_SNAPSHOT_KEY = "wt_active_snapshot";
export function cacheActiveSession(s) {
  try {
    if (s) localStorage.setItem(ACTIVE_SNAPSHOT_KEY, JSON.stringify(s));
    else localStorage.removeItem(ACTIVE_SNAPSHOT_KEY);
  } catch {}
}
function loadCachedSession() {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_SNAPSHOT_KEY)) || null;
  } catch {
    return null;
  }
}

export async function refreshActiveSession() {
  // Drain first, for the same reason startSession does: while a completion is still queued the
  // server legitimately still reports that session as active, and adopting it here would
  // resurrect a workout the user already finished. A no-op when the queue is empty or offline.
  await outbox.flush();
  try {
    const s = await api.activeSession();
    activeSession(s);
    cacheActiveSession(s); // refresh (or clear) the snapshot from the server's truth
  } catch {
    // Offline / unreachable: fall back to the last cached session rather than dropping it.
    activeSession(loadCachedSession());
  }
  return activeSession();
}

/** Wipe all user-specific state upon logout, account deletion, or auth rejection. */
export function clearUserSession() {
  setToken(null);
  profile(null);
  activeSession(null);
  cacheActiveSession(null);
  clearFinishing();
  outbox.clear();
  try {
    localStorage.removeItem("body_map");
    localStorage.removeItem(ACTIVE_SNAPSHOT_KEY);
    localStorage.removeItem(FINISHING_KEY);
  } catch {}
}
onAuthError(clearUserSession);

export function getBodyMap() {
  const g = profile()?.gender;
  if (g === "female" || g === "male") return g;
  try {
    const stored = localStorage.getItem("body_map");
    if (stored === "female" || stored === "male") return stored;
  } catch {}
  return "male";
}
export function setBodyMap(sex) {
  try {
    if (sex === "female" || sex === "male") {
      localStorage.setItem("body_map", sex);
    } else {
      localStorage.removeItem("body_map");
    }
  } catch {}
}

export function applyTheme(theme) {
  localStorage.setItem("theme", theme);
  const dark = theme === "dark" || (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

// Exercise library is fetched once and cached (used by builder, picker, library screen).
// Also persisted to localStorage so the picker — hence offline add-exercise — keeps working
// after a reload with no connection (the catalog is large but effectively static).
const LIBRARY_KEY = "wt_library_cache";
let _libraryCache = null;
export async function loadLibrary() {
  if (_libraryCache) return _libraryCache;
  try {
    _libraryCache = await api.exercises();
    try { localStorage.setItem(LIBRARY_KEY, JSON.stringify(_libraryCache)); } catch {}
  } catch (e) {
    // Offline / unreachable: fall back to the last persisted catalog so the picker still opens.
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(LIBRARY_KEY)); } catch {}
    if (Array.isArray(cached) && cached.length) return (_libraryCache = cached);
    throw e;
  }
  return _libraryCache;
}
// Drop the cache so the next loadLibrary() re-fetches (after creating an exercise, etc.).
export function invalidateLibrary() {
  _libraryCache = null;
}
